import {
  type FormkitNode,
  type RelationshipHierarchy,
  NodeType,
} from "@hightouch/formkit";
import get from "lodash/get";
import has from "lodash/has";
import uniq from "lodash/uniq";

import type { OverrideConfig } from "src/components/destinations/types";
import { isFieldEmpty } from "./utils";

/**
 * Get all ancestors of a key, recursively
 */
function getAllAncestors(
  key: string,
  relationshipHierarchy: RelationshipHierarchy,
  visited: Set<string> = new Set(),
): string[] {
  if (visited.has(key)) {
    return [];
  }
  visited.add(key);
  const ancestors = Array.from(
    get(relationshipHierarchy, [key, "ancestors"], new Set<string>()),
  );
  const ancestorsOfAncestors = ancestors.flatMap((ancestorKey) =>
    getAllAncestors(ancestorKey, relationshipHierarchy, visited),
  );

  return uniq([...ancestors, ...ancestorsOfAncestors]);
}

/**
 * Get the unlocked state of a field and whether any of its ancestors are unlocked
 */
export function getFieldContext({
  key,
  overrideConfig,
  relationshipHierarchy,
  templateConfig,
}: {
  overrideConfig: OverrideConfig | null | undefined;
  key: string;
  relationshipHierarchy: RelationshipHierarchy | null;
  templateConfig?: Record<string, any> | null;
}): {
  isUnlocked: boolean;
  isAnyAncestorUnlocked: boolean;
  fieldIsEmpty: boolean;
  isAnyAncestorEmpty: boolean;
} {
  const fieldIsEmpty = isFieldEmpty(key, templateConfig);

  if (!overrideConfig || !relationshipHierarchy) {
    return {
      isUnlocked: false,
      isAnyAncestorUnlocked: false,
      fieldIsEmpty,
      isAnyAncestorEmpty: false,
    };
  }

  const isUnlocked = get(overrideConfig, [key, "overridable"], false);

  // Get all ancestors of the key, and all ancestors of the ancestors
  const ancestors = getAllAncestors(key, relationshipHierarchy);
  const isAnyAncestorUnlocked = ancestors.some((ancestor) =>
    get(overrideConfig, [ancestor, "overridable"], false),
  );

  // Check if any ancestor has an empty field in the template, following the same pattern as unlocked
  const isAnyAncestorEmpty = ancestors.some((ancestor) =>
    isFieldEmpty(ancestor, templateConfig),
  );

  return {
    isUnlocked,
    isAnyAncestorUnlocked,
    fieldIsEmpty,
    isAnyAncestorEmpty,
  };
}

/**
 * Determine if a node is visible by recursively checking the formkit definition
 * against the relationship hierarchy.
 *
 * TODO(Samuel): what's a better way to do this? Right now it'll check on every render.
 *
 * @param node - The node to check.
 * @param relationshipHierarchy - The relationship hierarchy of the form.
 * @param overrideConfig - The override config of the form.
 * @param templateConfig - The sync template configuration to check for empty fields.
 * @returns Whether the node is visible.
 */
export function isNodeVisible({
  node,
  relationshipHierarchy,
  overrideConfig = null,
  templateConfig = null,
}: {
  node: FormkitNode;
  relationshipHierarchy: RelationshipHierarchy | null;
  overrideConfig: OverrideConfig | null;
  templateConfig?: Record<string, any> | null;
}): boolean {
  // If we have template config but no override config or relationship hierarchy,
  // only show fields that are empty in the template
  if (overrideConfig === null || relationshipHierarchy === null) {
    if (templateConfig != null && node.type === NodeType.Component) {
      return isFieldEmpty(node.key, templateConfig);
    }
    // For layout nodes, check if any children should be visible
    if (templateConfig && node.children) {
      return node.children.some((child) =>
        isNodeVisible({
          node: child,
          relationshipHierarchy,
          overrideConfig,
          templateConfig,
        }),
      );
    }
    // If no template config, show everything
    return !templateConfig;
  }

  if (node.type === NodeType.Component) {
    // Check if field is explicitly locked (overridable: false)
    const fieldOverrideConfig = get(overrideConfig, [node.key]);
    if (fieldOverrideConfig && !fieldOverrideConfig.overridable) {
      return false;
    }
    // If node is unlocked or empty, this node must be visible
    const {
      isUnlocked,
      isAnyAncestorUnlocked,
      fieldIsEmpty,
      isAnyAncestorEmpty,
    } = getFieldContext({
      key: node.key,
      overrideConfig,
      relationshipHierarchy,
      templateConfig,
    });
    const fieldIsExplicitlyEmpty =
      has(templateConfig ?? {}, node.key) && fieldIsEmpty;
    if (!fieldOverrideConfig && templateConfig && !fieldIsExplicitlyEmpty) {
      return false;
    }

    // If this field or any of the ancestors are unlocked, or if the field or any ancestor is empty in template, this node must be visible
    if (
      isUnlocked ||
      isAnyAncestorUnlocked ||
      fieldIsEmpty ||
      isAnyAncestorEmpty
    ) {
      return true;
    }
  }

  // If any of the children are unlocked, this node must be visible
  if (node.children) {
    return node.children.some((child) =>
      isNodeVisible({
        node: child,
        relationshipHierarchy,
        overrideConfig,
        templateConfig,
      }),
    );
  }

  return false;
}
